import { CartContext } from 'constants/contexts';
import React, {
  useEffect, useState, useMemo, useContext,
} from 'react';
import dayjs from 'dayjs';
import { isDateToday, filterTimesAfterNow, isSameDay } from 'utils/date';
import {
  Box, Button, Select,
  Typography,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import http from 'services/http';
import Overlay from 'components/Overlay';

function ScheduleOrderOverlay({
  merchant, open, onClose, value, cancelButton, triggerAction = null,
}) {
  const cart = useContext(CartContext);
  const [dates, setDates] = useState();
  const [selectedDate, setSelectedDate] = useState();
  const [selectedTime, setSelectedTime] = useState();

  useEffect(() => {
    if (!merchant?.id) {
      return;
    }
    http
      .post(`/v1/restaurants/${merchant.id}/schedule-times/`, {
        items: cart.items.filter((i) => i.restaurant === merchant.id).map((i) => i.id),
      })
      .then(({ data: { data } }) => {
        setDates(
          Object.entries(data)
            .filter(([_, times]) => times.length > 0)
            .map(([date, times], index) => ({
              index,
              title: dayjs(date).format('ddd, DD MMM'),
              date,
              times,
            })),
        );
      });
  }, [merchant?.id, cart.items]);

  const handleSelectDate = ({ target: { value: index } }) => {
    setSelectedDate(dates[index]);
  };
  const timeOptions = useMemo(() => {
    if (!selectedDate) {
      return [];
    }
    const { times, date } = selectedDate;
    const isToday = isDateToday(date);
    return times
      .reduce((all, d) => [...all, ...(isToday ? filterTimesAfterNow(d.slots) : d.slots)], [])
      .map((s, index) => ({
        index,
        title: `${formatTime(s.start)} - ${formatTime(s.end)}`,
        time: s,
      }));
  }, [selectedDate]);

  useEffect(() => {
    if (!dates || !dates[0]) {
      return;
    }
    if (selectedDate) {
      return;
    }
    if (!value) {
      setSelectedDate(dates[0]);
      return;
    }
    const currentDate = dates.find((d) => isSameDay(new Date(d.date), new Date(value)));
    setSelectedDate(currentDate || dates[0]);
  }, [dates, value, selectedDate]);

  const handleSelectTime = ({ target: { value: index } }) => {
    setSelectedTime(timeOptions[index]);
  };

  function getDateObject(date, time) {
    return new Date(`${date}T${time}`);
  }

  const handleSubmit = () => {
    if (selectedDate == null && selectedTime == null) {
      return;
    }
    cart.setScheduledTime(
      getDateObject(selectedDate.date, selectedTime.time.start).getTime(),
    );
    onClose();
  };

  if (!dates?.length) {
    return null;
  }
  function formatTime(time) {
    return dayjs(time, 'HH:mm:ss').format('hh:mm A');
  }
  const opensDate = dayjs(dates[0].date).format('MMM DD');
  const opensTime = formatTime(dates[0].times[0].start);

  return (
    <>
      {triggerAction}
      <Overlay open={open && dates?.length} onClose={onClose}>

        <Overlay.Header>
          {
            merchant?.takeout ? 'Schedule Your Order' :
              `Opens ${opensDate} at ${opensTime}`
          }
        </Overlay.Header>
        <Overlay.Content>
          <Typography>
            { !merchant?.takeout && 'On-demand ordering is currently not available.' }
            {' '}
            You can schedule your
            order for the next available time.
          </Typography>
          <div>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel htmlFor="app-select-date">Date</InputLabel>
              <Select
                labelId="app-select-date-label"
                id="app-select-date"
                value={selectedDate?.index}
                onChange={handleSelectDate}
                input={<OutlinedInput label="Date" />}
              >
                {dates.map((option) => (
                  <MenuItem
                    value={option.index}
                    key={`${option.index} - ${option.title}`}
                  >
                    {option.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <FormControl
              fullWidth
              sx={{ mt: 2 }}
              disabled={selectedDate == null || timeOptions.length === 0}
            >
              <InputLabel id="app-select-time-label">Time</InputLabel>
              <Select
                labelId="app-select-time-label"
                id="app-select-time"
                value={selectedTime?.index}
                onChange={handleSelectTime}
                input={<OutlinedInput label="Time" />}
              >
                {timeOptions.map((o) => (
                  <MenuItem value={o.index} key={`${o.index} - ${o.title}`}>
                    {o.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <Box mt={4} display="flex" flexDirection="column" gap={2}>
            <Button
              size="large"
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={selectedDate == null || selectedTime == null}
            >
              Select Time
            </Button>
            {cancelButton || (
              <Button
                size="large"
                variant="outlined"
                fullWidth
                onClick={onClose}
              >
                cancel
              </Button>
            )}
          </Box>
        </Overlay.Content>
      </Overlay>
    </>
  );
}

export default ScheduleOrderOverlay;
